// TTC counterpart of NearpodNext's `views/slides/components/rich_text/accessible_rich_text_renderer.jsx`.
// Same contract: detect rich text, sanitize, write it into the DOM, typeset the formulas, and hand
// back the screen-reader text. Plain text takes the textContent path untouched.
//
// Three deliberate differences from NearpodNext: it renders the caller's own element (`as`) rather
// than adding a wrapper <div>, which the tight flex/absolute layouts in Question and Answer cannot
// take; the speech callback lives in a ref, so an inline arrow does not re-typeset every render; and
// it can put the speech in a visually hidden sibling instead of an aria-label (`speechOutlet`), which
// is the only outlet that works on the display-only elements TTC renders formulas into.

import React, { useEffect, useRef, useState } from 'react'

import './RichTextContent.css'
import ScreenReaderOnly from '../screenReaderOnly/ScreenReaderOnly'
import { renderMathInto, renderMathClear } from '../../helpers/math/mathjax'
import alignFormulasToText from '../../helpers/math/formulaAlignment'
import { extractMathSpeech } from '../../helpers/math/mathSpeech'
import sanitizeRichText, { ensurePlainText } from '../../helpers/html/sanitizer'
import isRichTextContent from '../../helpers/html/richTextContent'

export interface IRichTextContentProps {
	content: string
	// Element type to render: a styled-component or a tag name. Defaults to a plain div.
	as?: React.ElementType
	// Receives the text a screen reader should announce for `content`: MathJax's formula speech
	// once typesetting settles, or the plain string when there is no math. TTC usually needs it on
	// a different element than this one (the answer's hidden input, the question's own aria-label),
	// which is why it is handed out instead of applied here.
	onSpeechChange?: (speech: string) => void
	// Whether the formula itself is a focus/pointer target. True by default, as in NearpodNext:
	// MathJax's Explorer lets a student walk a formula subexpression by subexpression, which is the
	// point of the feature. Pass false only when an enclosing control owns interaction and would
	// lose it to the formula — a TTC answer is a <label> for a hidden input, so a click landing on
	// the formula would not select the answer. Ignored under speechOutlet='hiddenText', which is
	// non-interactive by construction: a tab stop inside an aria-hidden subtree is invalid.
	isInteractive?: boolean
	// Where the speech for `content` goes, for the common case where this element is what a screen
	// reader reaches — typeset glyphs are meaningless to announce. Prefer either outlet to wiring
	// onSpeechChange into a parent's state; reach for onSpeechChange only when the accessible name
	// belongs to a different element (a TTC answer names its hidden input, not the text).
	//   'label'      the speech becomes this element's aria-label. Only valid when the element also
	//                carries a role: naming a generic element is prohibited in ARIA 1.2, so Chrome and
	//                Firefox drop an aria-label on a plain div or span. Question's text sets
	//                role="heading", so aria-labelledby and aria-describedby resolve through it.
	//   'hiddenText' the element is marked aria-hidden and the speech is rendered in a visually hidden
	//                sibling. The right outlet for a display-only element with no role, and the only
	//                one that works inside a live region: a region is announced from its content, not
	//                from an accessible name, and typeset formulas contribute no text since MathJax
	//                hides its glyphs and assistiveMml is off.
	speechOutlet?: 'label' | 'hiddenText'
	// Formulas are sized to sit alongside the surrounding text automatically. Pass false to render
	// them at their natural size instead, which a context whose font has unusual metrics needs — see
	// ITypesetOptions for what "unusual" means here.
	autoFontSizing?: boolean
	// How heavily the formula's glyphs are weighted. Reach for 'light' wherever the surrounding text
	// is small, roughly 16px and below, since a stroke that suits question text reads as blobby at
	// that size. The widths live in RichTextContent.css.
	formulaWeight?: 'regular' | 'light'
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[passThroughProp: string]: any
}

// Styled in RichTextContent.css: makes the typeset formula inert to the mouse. The keyboard half is the
// tabIndex = -1 pass below; both are needed to fully hand interaction to an enclosing control.
const NON_INTERACTIVE_CLASS = 'mjx-non-interactive'

// Also in RichTextContent.css: the lighter of the two glyph weights.
const LIGHT_FORMULA_CLASS = 'rich-text-formula-light'

// The speech to hold until the effect publishes the real one. Plain text is already its own speech and
// is byte-identical to what the effect will publish, so it can be final from the first render, which
// keeps a live region from mounting empty. Rich text has to stay empty: its speech only exists in the
// typeset DOM, and the plain-text fallback would make a region announce the raw TeX first and the
// speech second.
const seedSpeech = (content: string) => (isRichTextContent(content) ? '' : content)

const RichTextContent = ({
	content,
	as,
	onSpeechChange,
	isInteractive = true,
	speechOutlet,
	autoFontSizing,
	formulaWeight = 'regular',
	...passThroughProps
}: IRichTextContentProps) => {
	const elementRef = useRef<HTMLElement | null>(null)
	const [speech, setSpeech] = useState(() => seedSpeech(content))

	// The speech must never outlive the content it describes: it feeds an aria-label or a live region,
	// so one stale frame is one wrong announcement. Reset during render rather than at the top of the
	// effect — effects run after paint, which is exactly the frame a live region reads.
	const [speechContent, setSpeechContent] = useState(content)
	if (speechContent !== content) {
		setSpeechContent(content)
		setSpeech(seedSpeech(content))
	}

	// A tab stop inside an aria-hidden subtree is unreachable and invalid, so the hidden-text outlet
	// takes the same path that strips MathJax's tab stops.
	const isFormulaInteractive = isInteractive && speechOutlet !== 'hiddenText'

	// See note 2 in the header: kept out of the effect's dependencies on purpose.
	const onSpeechChangeRef = useRef(onSpeechChange)
	onSpeechChangeRef.current = onSpeechChange

	const publishSpeech = (value: string) => {
		setSpeech(value)
		if (onSpeechChangeRef.current) onSpeechChangeRef.current(value)
	}
	const publishSpeechRef = useRef(publishSpeech)
	publishSpeechRef.current = publishSpeech

	useEffect(() => {
		const element = elementRef.current
		if (!element) return

		if (!isRichTextContent(content)) {
			element.textContent = content
			publishSpeechRef.current(content)
			return
		}

		element.innerHTML = sanitizeRichText(content)

		const controller = new AbortController()
		// renderMathInto never rejects — it falls back to raw LaTeX internally — so .then() is the
		// point where the rendered DOM is final and its speech can be harvested.
		renderMathInto(element, controller.signal, { matchFontHeight: autoFontSizing }).then(() => {
			if (controller.signal.aborted) return
			if (!isFormulaInteractive) {
				// MathJax's Explorer marks the container tabindex="0". The container only exists once
				// typesetting settles, so the tab stop has to be removed here rather than up front.
				element.querySelectorAll<HTMLElement>('mjx-container').forEach((container) => {
					container.tabIndex = -1
				})
			}
			alignFormulasToText(element)
			publishSpeechRef.current(extractMathSpeech(element))
		})

		return () => {
			controller.abort()
			renderMathClear(element)
		}
	}, [content, isFormulaInteractive, autoFontSizing])

	// styled-components merges an incoming className with its own generated one, so the caller's
	// styling survives.
	const className =
		[
			passThroughProps.className,
			isFormulaInteractive ? '' : NON_INTERACTIVE_CLASS,
			formulaWeight === 'light' ? LIGHT_FORMULA_CLASS : '',
		]
			.filter(Boolean)
			.join(' ') || undefined

	// ensurePlainText covers the gap before MathJax resolves; an empty aria-label would leave the
	// element unnamed. An explicit aria-label from the caller still wins.
	const outletProps: Record<string, string> = {}
	if (speechOutlet === 'label' && !passThroughProps['aria-label']) {
		outletProps['aria-label'] = speech || ensurePlainText(content)
	} else if (speechOutlet === 'hiddenText') {
		outletProps['aria-hidden'] = 'true'
	}

	const element = React.createElement(as || 'div', { ...passThroughProps, ...outletProps, className, ref: elementRef })

	if (speechOutlet !== 'hiddenText') return element

	// The sibling is absent while the speech is empty, so nothing announces before MathJax resolves.
	// Silence is not a risk: renderMathInto falls back to raw LaTeX instead of failing, so speech
	// always arrives.
	return (
		<>
			{element}
			{!!speech && <ScreenReaderOnly>{speech}</ScreenReaderOnly>}
		</>
	)
}

export default RichTextContent
